/**
 * Reload loop safeguard — sonsuz reload döngüsü koruması (İKİ pencereli — K7).
 *
 *   - HIZLI loop:  5dk içinde 3+ reload  -> fatal mode (fp-fatal-error tam ekran).
 *   - YAVAŞ loop:  1sa içinde 8+ reload   -> fatal mode. (Eski tek-pencere mantığı 5dk'dan
 *                  seyrek tekrarlayan reload'da sayacı her seferinde sıfırlıyor, fatal'a
 *                  hiç girmiyordu → sonsuz yavaş flicker. Uzun pencere bunu yakalar.)
 *
 * OTA update reload'ları bypass eder (skipGuard=true) — bilinçli yeniden yükleme
 * loop sayılmamalı.
 *
 * Memory: decision_pwa_resilience.md §4 + faz-1.18-pwa-ux-denetimi.md K7
 */
interface ReloadLog {
    count: number;
    window_start: number;
    reasons: string[];
    long_start: number;
    long_count: number;
}

const KEY = 'fp:reload_log';
const WINDOW_MS = 5 * 60 * 1000;
const MAX_RELOADS = 3;
const LONG_WINDOW_MS = 60 * 60 * 1000;
const LONG_MAX_RELOADS = 8;
const MAX_REASONS = 12;

class ReloadGuard {
    private read(): ReloadLog {
        const fresh = (): ReloadLog => ({
            count: 0,
            window_start: Date.now(),
            reasons: [],
            long_start: Date.now(),
            long_count: 0,
        });
        try {
            const raw = localStorage.getItem(KEY);
            if (!raw) return fresh();
            const parsed = JSON.parse(raw) as Partial<ReloadLog>;
            // Eski şema (long_* yok) ile geriye uyum.
            return {
                count: parsed.count ?? 0,
                window_start: parsed.window_start ?? Date.now(),
                reasons: parsed.reasons ?? [],
                long_start: parsed.long_start ?? Date.now(),
                long_count: parsed.long_count ?? 0,
            };
        } catch {
            return fresh();
        }
    }

    private write(log: ReloadLog): void {
        try {
            localStorage.setItem(KEY, JSON.stringify(log));
        } catch {
            // localStorage dolu / kapalı — sessizce geç
        }
    }

    /**
     * Reload talebi. Loop tespit edilirse fatal mode'a girer.
     * @param skipGuard true -> loop sayımı atla (OTA gibi bilinçli reload)
     */
    requestReload(reason: string, skipGuard = false): void {
        if (skipGuard) {
            this.doReload();
            return;
        }

        const log = this.read();
        const now = Date.now();

        // Uzun pencere (yavaş loop): süresi dolduysa sıfırla, değilse say.
        if (now - log.long_start > LONG_WINDOW_MS) {
            log.long_start = now;
            log.long_count = 0;
        }
        log.long_count++;

        // Kısa pencere (hızlı loop): süresi dolduysa sıfırla, değilse say.
        if (now - log.window_start > WINDOW_MS) {
            log.window_start = now;
            log.count = 0;
        }
        log.count++;

        log.reasons.push(reason);
        if (log.reasons.length > MAX_REASONS) {
            log.reasons = log.reasons.slice(-MAX_REASONS);
        }
        this.write(log);

        if (log.count >= MAX_RELOADS || log.long_count >= LONG_MAX_RELOADS) {
            console.error('[reload-guard] LOOP DETECTED', log);
            this.enterFatalMode(log.reasons);
            return;
        }

        this.doReload();
    }

    private doReload(): void {
        // Dexie flush vb. için kısa gecikme
        setTimeout(() => window.location.reload(), 100);
    }

    private enterFatalMode(reasons: string[]): void {
        const el = document.createElement('fp-fatal-error');
        el.setAttribute('reasons', reasons.join(' | '));
        document.body.innerHTML = '';
        document.body.appendChild(el);
    }

    /** Sayaç sıfırla (sağlıklı uptime sonrası çağrılabilir). */
    reset(): void {
        try {
            localStorage.removeItem(KEY);
        } catch {
            // noop
        }
    }
}

export const reloadGuard = new ReloadGuard();
